import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, serverTimestamp, query, collection, where, getDocs } from "firebase/firestore";
import { db, auth } from "../services/firebase";
import { useUserData } from "../hooks/useUserData";
import { User, Phone, GraduationCap, CheckCircle, ArrowRight } from "lucide-react";

export default function StudentProfileSetup() {
  const navigate = useNavigate();
  const [user, userLoading] = useUserData();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    surname: "",
    lrn: "",
    gradeYearLevel: "",
    section: "",
    contactNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Redirect if not logged in or not a student
  useEffect(() => {
    if (!userLoading && (!user || user.role !== "student")) {
      navigate("/login");
    }
  }, [user, userLoading, navigate]);

  // Check if profile is already complete
  useEffect(() => {
    if (user && user.firstName && user.surname && user.lrn && user.gradeYearLevel && user.section && user.contactNumber) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const steps = [
    {
      title: "Personal Information",
      description: "Let's start with your basic details",
      fields: ["firstName", "middleName", "surname"],
      icon: <User className="w-6 h-6" />,
    },
    {
      title: "Academic Details",
      description: "Tell us about your academic information",
      fields: ["lrn", "gradeYearLevel", "section"],
      icon: <GraduationCap className="w-6 h-6" />,
    },
    {
      title: "Contact Information",
      description: "How can we reach you?",
      fields: ["contactNumber"],
      icon: <Phone className="w-6 h-6" />,
    },
  ];

  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case "firstName":
      case "surname":
        if (!value.trim()) {
          newErrors[field] = "This field is required";
        } else if (value.length < 2) {
          newErrors[field] = "Must be at least 2 characters";
        } else {
          delete newErrors[field];
        }
        break;
      case "lrn":
        if (!value.trim()) {
          newErrors[field] = "LRN is required";
        } else if (!/^\d{12}$/.test(value)) {
          newErrors[field] = "LRN must be exactly 12 digits";
        } else {
          delete newErrors[field];
        }
        break;
      case "gradeYearLevel":
        if (!value) {
          newErrors[field] = "Please select your grade/year level";
        } else {
          delete newErrors[field];
        }
        break;
      case "section":
        if (!value.trim()) {
          newErrors[field] = "Section is required";
        } else {
          delete newErrors[field];
        }
        break;
      case "contactNumber":
        if (!value.trim()) {
          newErrors[field] = "Contact number is required";
        } else if (!/^(\+63|0)[9]\d{9}$/.test(value)) {
          newErrors[field] = "Please enter a valid Philippine mobile number";
        } else {
          delete newErrors[field];
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const handleNext = () => {
    const currentStepFields = steps[currentStep].fields;
    const isValid = currentStepFields.every(field => validateField(field, formData[field]));

    if (isValid) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // Final validation
    const allFields = Object.keys(formData);
    const isValid = allFields.every(field => validateField(field, formData[field]));

    if (!isValid) return;

    setIsSubmitting(true);
    try {
      // Check LRN uniqueness
      const lrnQuery = query(collection(db, "users"), where("lrn", "==", formData.lrn));
      const lrnSnapshot = await getDocs(lrnQuery);

      if (!lrnSnapshot.empty) {
        const newErrors = { ...errors };
        newErrors.lrn = "This LRN is already registered by another user";
        setErrors(newErrors);
        return;
      }

      const userRef = doc(db, "users", auth.currentUser.uid);
      const displayName = `${formData.firstName} ${formData.surname}`;

      if (!auth.currentUser) {
        throw new Error("No user is logged in");
      }

      await setDoc(userRef, {
        firstName: formData.firstName,
        middleName: formData.middleName || "",
        surname: formData.surname,
        lrn: formData.lrn,
        gradeYearLevel: formData.gradeYearLevel,
        section: formData.section,
        contactNumber: formData.contactNumber,
        name: displayName,
        email: auth.currentUser.email,
        role: "student",
        profileCompleted: true,
        profileCompletedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Small delay to ensure Firestore operations complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      navigate("/dashboard");
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("Failed to save profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const fieldConfig = {
      firstName: {
        label: "First Name",
        type: "text",
        placeholder: "Enter your first name",
        required: true,
      },
      middleName: {
        label: "Middle Name (Optional)",
        type: "text",
        placeholder: "Enter your middle name",
        required: false,
      },
      surname: {
        label: "Surname",
        type: "text",
        placeholder: "Enter your surname",
        required: true,
      },
      lrn: {
        label: "Learner Reference Number (LRN)",
        type: "text",
        placeholder: "12-digit LRN",
        required: true,
        maxLength: 12,
      },
      gradeYearLevel: {
        label: "Grade/Year Level",
        type: "select",
        required: true,
        options: [
          "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12"
        ],
      },
      section: {
        label: "Section",
        type: "text",
        placeholder: "Enter your section (e.g., A, B, 1, 2)",
        required: true,
      },
      contactNumber: {
        label: "Contact Number",
        type: "tel",
        placeholder: "09XXXXXXXXX or +639XXXXXXXXX",
        required: true,
      },
    };

    const config = fieldConfig[field];

    return (
      <div key={field} className="space-y-2">
        <label className="form-label text-white">
          {config.label}
          {config.required && <span className="text-red-400 ml-1">*</span>}
        </label>
        {config.type === "select" ? (
          <select
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="form-input bg-surface-200/50 border-surface-300/50 text-white placeholder-neutral-400 focus:border-primary-400 focus:ring-primary-400"
            required={config.required}
          >
            <option value="">Select {config.label.toLowerCase()}</option>
            {config.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        ) : (
          <input
            type={config.type}
            placeholder={config.placeholder}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            maxLength={config.maxLength}
            className="form-input bg-surface-200/50 border-surface-300/50 text-white placeholder-neutral-400 focus:border-primary-400 focus:ring-primary-400"
            required={config.required}
          />
        )}
        {errors[field] && (
          <p className="text-red-400 text-sm">{errors[field]}</p>
        )}
      </div>
    );
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div
      className="relative flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat animate-fade-in"
      style={{
        backgroundImage: `url('/cathsbgfinal.png')`,
      }}
    >
      {/* Enhanced gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-900/60 via-primary-900/40 to-neutral-900/60"></div>

      <div className="relative z-10 w-full max-w-md mx-4 animate-scale-in">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white text-sm font-medium">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-white text-sm">{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-surface-200/30 rounded-full h-2">
            <div
              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Step Card */}
        <div className="glass-card p-6">
          {/* Step Header */}
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
              {currentStepData.icon}
            </div>
            <h1 className="text-xl font-bold text-white mb-1">
              {currentStepData.title}
            </h1>
            <p className="text-neutral-300 text-sm">
              {currentStepData.description}
            </p>
          </div>

          {/* Form Fields */}
          <div className="space-y-4 mb-6">
            {currentStepData.fields.map(field => renderField(field))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="btn-secondary flex-1 py-3"
                disabled={isSubmitting}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={isSubmitting || Object.keys(errors).length > 0}
              className="btn-primary flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </>
              ) : currentStep === steps.length - 1 ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Complete</span>
                </>
              ) : (
                <>
                  <span>Next</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-surface-300/30">
            <p className="text-center text-xs text-white">
              Your information is securely stored and will be used for asset management purposes only.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
